import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Check, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicClient = axios.create({ baseURL: API });

// ---------------------------------------------------------------------------
// Public, no-auth page for an insured to fill the RPS quote sheet via a
// one-time link emailed from SuperAdmin.  Visually branded as a generic
// "RPS Quote Sheet" intake, not ChildCheckOh.
// ---------------------------------------------------------------------------
export default function QuoteFillPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, form: null, data: {}, insured: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Safety net for "Link not found": some links were generated on preview
  // before the host-aware fix landed, so they point to preview but the
  // token actually lives in the production DB.  If we're on preview and the
  // public lookup fails, hand off to production.  Operators can override
  // this with ?nofallback=1 for local testing.
  const PROD_HOST = "childcheckoh.com";
  const isPreview = typeof window !== "undefined" && /preview\.emergentagent\.com$/i.test(window.location.hostname);
  const allowFallback = typeof window !== "undefined" && !new URLSearchParams(window.location.search).has("nofallback");

  useEffect(() => {
    publicClient
      .get(`/public/quote-fill/${token}`)
      .then((r) => setState({ loading: false, error: null, form: r.data.form, data: r.data.prefill || {}, insured: r.data.insured_label || "" }))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404 && isPreview && allowFallback) {
          window.location.replace(`https://${PROD_HOST}/quote-fill/${token}`);
          return;
        }
        setState({ loading: false, error: err?.response?.data?.detail || err.message, form: null, data: {}, insured: "" });
      });
  }, [token, isPreview, allowFallback]);

  const update = (id, v) => setState((s) => ({ ...s, data: { ...s.data, [id]: v } }));
  const updateRow = (key, idx, colId, v) => {
    setState((s) => {
      const rows = [...(s.data[key] || [])];
      while (rows.length <= idx) rows.push({});
      rows[idx] = { ...rows[idx], [colId]: v };
      return { ...s, data: { ...s.data, [key]: rows } };
    });
  };

  const submit = async () => {
    setSaving(true);
    try {
      await publicClient.post(`/public/quote-fill/${token}`, { data: state.data });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setSaving(false); }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50">
        <Loader2 className="w-6 h-6 animate-spin text-sky-700" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 px-4">
        <div data-testid="qf-error" className="max-w-md rounded-2xl border border-stone-200 bg-white p-8 text-center shadow-sm">
          <Truck className="w-10 h-10 mx-auto text-stone-400 mb-4" />
          <div className="font-display text-xl text-stone-900">This link can't be opened</div>
          <div className="text-sm text-stone-600 mt-2">{state.error}</div>
          <div className="text-xs text-stone-500 mt-4">If you've already submitted this form, you're all set. Otherwise reply to the email and we'll resend a fresh link.</div>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 px-4">
        <div data-testid="qf-success" className="max-w-md rounded-2xl border border-emerald-200 bg-white p-8 text-center shadow-sm">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 grid place-items-center mb-4">
            <Check className="w-7 h-7 text-emerald-700" />
          </div>
          <div className="font-display text-2xl text-stone-900">Thanks — submission received.</div>
          <div className="text-sm text-stone-600 mt-2">
            We'll review your answers and follow up with a quote shortly.
          </div>
        </div>
      </div>
    );
  }

  const f = state.form;
  const sections = _groupBySection(f.fields || []);
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-stone-50 to-stone-50 pb-20">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-700 text-white grid place-items-center">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-sky-700 font-semibold">RPS Quote Sheet</div>
            <div className="font-display text-lg text-stone-900">{f.name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 pt-6 space-y-5">
        <div className="rounded-2xl border border-sky-200 bg-white p-4">
          <div className="text-xs text-stone-700 leading-relaxed">
            <Sparkles className="inline w-3.5 h-3.5 text-sky-700 mr-1 align-text-bottom" />
            Hi{state.insured ? ` ${state.insured}` : ""}, please complete the fields below.  Your responses are sent directly to the brokerage when you click <strong>Submit</strong>.  Anything you don't know can be left blank.
          </div>
        </div>

        {(f.header_fields || []).length > 0 && (
          <Section title="Quote basics">
            <FieldGrid fields={f.header_fields} data={state.data} update={update} />
          </Section>
        )}
        {sections.map(([sect, fields]) => (
          <Section key={sect} title={sect}>
            <FieldGrid fields={fields} data={state.data} update={update} />
          </Section>
        ))}

        <TableCard title="Vehicles" cfg={f.vehicle_table} tableKey="vehicles" data={state.data} updateRow={updateRow} />
        <TableCard title="Drivers" cfg={f.driver_table} tableKey="drivers" data={state.data} updateRow={updateRow} />
        <TableCard title="Commodities" cfg={f.commodities_table} tableKey="commodities" data={state.data} updateRow={updateRow} />
        <TableCard title="Revenue / mileage history" cfg={f.history_table} tableKey="history" data={state.data} updateRow={updateRow} rowLabels={f.history_table?.row_labels} />

        <div className="sticky bottom-3 mt-6">
          <div className="rounded-2xl border border-stone-200 bg-white shadow-lg p-3 flex items-center justify-between gap-3">
            <div className="text-xs text-stone-500">Your answers are submitted to the brokerage when you click Submit.</div>
            <Button
              data-testid="qf-submit"
              onClick={submit}
              disabled={saving}
              className="bg-sky-700 hover:bg-sky-800 text-white rounded-lg h-10 px-5 text-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Submit quote
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function FieldGrid({ fields, data, update }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {fields.map((f) => <FillField key={f.id} field={f} value={data[f.id]} onChange={(v) => update(f.id, v)}
                                    extraValue={f.extra_id ? data[f.extra_id] : undefined}
                                    onExtraChange={(v) => f.extra_id && update(f.extra_id, v)} />)}
    </div>
  );
}

function FillField({ field, value, onChange, extraValue, onExtraChange }) {
  const tid = `qf-${field.id}`;
  if (field.type === "yesno") {
    return (
      <div className="rounded-xl border border-stone-200 p-3 bg-stone-50/40">
        <div className="text-xs font-medium text-stone-800 mb-2">{field.label}</div>
        <div className="flex gap-2 items-center flex-wrap">
          {["yes", "no"].map((opt) => (
            <button
              key={opt}
              data-testid={`${tid}-${opt}`}
              type="button"
              onClick={() => onChange(opt === value ? "" : opt)}
              className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                (value || "") === opt
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-stone-700 border-stone-300 hover:bg-stone-100"
              }`}
            >{opt.toUpperCase()}</button>
          ))}
          {field.extra_id && (
            <input
              data-testid={`${tid}-extra`}
              type="text"
              placeholder="If yes, which units?"
              value={extraValue || ""}
              onChange={(e) => onExtraChange(e.target.value)}
              className="flex-1 min-w-[140px] h-8 px-2 text-xs rounded border border-stone-300 focus:border-sky-600 outline-none"
            />
          )}
        </div>
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label data-testid={tid} className="flex items-center gap-2 rounded-xl border border-stone-200 p-3 bg-stone-50/40 cursor-pointer hover:bg-stone-50">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-emerald-700" />
        <span className="text-xs font-medium text-stone-800">{field.label}</span>
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="sm:col-span-2 rounded-xl border border-stone-200 p-3 bg-stone-50/40">
        <div className="text-xs font-medium text-stone-800 mb-1">{field.label}</div>
        <textarea
          data-testid={tid}
          rows={2}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-2 py-1.5 text-xs rounded border border-stone-300 focus:border-sky-600 outline-none resize-none"
        />
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-stone-200 p-3 bg-stone-50/40">
      <div className="text-xs font-medium text-stone-800 mb-1">{field.label}</div>
      <input
        data-testid={tid}
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-8 px-2 text-xs rounded border border-stone-300 focus:border-sky-600 outline-none"
      />
    </div>
  );
}

function TableCard({ title, cfg, tableKey, data, updateRow, rowLabels }) {
  if (!cfg) return null;
  const rows = data[tableKey] || [];
  return (
    <Section title={title}>
      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-stone-500">
            <tr>
              {rowLabels && <th className="text-left px-2 py-1.5">Period</th>}
              {cfg.columns.map((c) => <th key={c.id} className="text-left px-2 py-1.5">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: cfg.rows }).map((_, idx) => (
              <tr key={idx} className="border-t border-stone-100">
                {rowLabels && <td className="px-2 py-1.5 text-stone-600">{rowLabels[idx] || ""}</td>}
                {cfg.columns.map((c) => (
                  <td key={c.id} className="px-1 py-1">
                    <input
                      data-testid={`qf-${tableKey}-${idx}-${c.id}`}
                      type="text"
                      value={(rows[idx] || {})[c.id] || ""}
                      onChange={(e) => updateRow(tableKey, idx, c.id, e.target.value)}
                      className="w-full h-8 px-2 text-xs rounded border border-stone-200 focus:border-sky-600 outline-none"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function _groupBySection(fields) {
  const out = new Map();
  fields.forEach((f) => {
    const k = f.section || "General";
    if (!out.has(k)) out.set(k, []);
    out.get(k).push(f);
  });
  return Array.from(out.entries());
}
